import "./App.css";
import { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { BrowserRouter } from "react-router-dom";
import Router from "./Infrastructure/Router/router";
import * as amplitude from "@amplitude/analytics-browser";
import ScrollToTop from "./Infrastructure/Helper/scrollToTop";
import { AuthProvider } from "./Infrastructure/States/authContext";
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import useAuthentication from "./Infrastructure/States/onAuthStateChange";
import MaintenancePage from "./Views/MaintenancePage/maintenancePage";
import { useDispatch, useSelector } from "react-redux";
import { HomePageSkeleton } from "./Views/Skeletons/HomePageSkeleton.jsx";
import { fetchFlags } from "./Infrastructure/States/flagsSlice";
import NoInternetPage from "./Views/NoInternetPage/noInternetPage";
import useMonitorEmailVerify from "./Infrastructure/CustomHooks/useMonitorEmailVerify";
import { ErrorModal } from "./Views/Common/ErrorComponents/errorModal";
import { signOut } from "firebase/auth";
import { ShowErrorToast } from "./Views/Common/Toast/toast";
import ERROR_CONSTANTS from "./Infrastructure/Constants/errorConstants";
import { auth } from "./Infrastructure/config";
import { HelmetProvider } from "react-helmet-async";
import { resetUserData } from "./Infrastructure/States/userDataSlice";
import './Infrastructure/Utilities/quillConfig.js'
import { trackEvent } from "./Infrastructure/Utilities/analytics.js";
import AnalyticsConstants from "./Infrastructure/Constants/AnalyticsContants.js";
import { fetchPublicIP } from "./Infrastructure/States/ipSlice.js";
import { safeClientLogEvent } from "./Infrastructure/Utilities/safeClientLogEvent.js";
import { UNAUTH_LOGS_CLIENT } from "./Infrastructure/Constants/logsConstant.js";

function App() {
  const dispatch = useDispatch()
  const { userId } = useAuthentication()
  const flags = useSelector((state) => state.flags.flags)
  const loading = useSelector((state) => state.flags.loading)
  const userData = useSelector((state) => state.userData.data)
  const userDataError = useSelector((state) => state.userData.error)
  useEffect(() => {
    if ((userDataError) && auth.currentUser) {
      signOut(auth)
        .then(() => {
          dispatch(resetUserData())
          ShowErrorToast(ERROR_CONSTANTS.GENERIC_ERROR_TOAST)
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [userDataError]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  useMonitorEmailVerify(userData);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    const initializeAmplitude = async () => {
      try {
        const config = {
          sampleRate: 1,
        };

        amplitude.init(process.env.REACT_APP_AMPLITUDE_API_KEY, undefined, {
          autocapture: {
            attribution: true,
            pageViews: false,
            sessions: true,
            formInteractions: true,
            fileDownloads: true,
            elementInteractions: true,
          },
        })

        const sessionReplayTracking = sessionReplayPlugin(config);
        await amplitude.add(sessionReplayTracking).promise;
        trackEvent(AnalyticsConstants.VULCAN_WEBSITE_LOADED);

      } catch (error) {
        safeClientLogEvent({
          ...UNAUTH_LOGS_CLIENT.AMPLITUDE_ERROR,
          source: "loginAccount.jsx",
          error: error.message,
          metadata: null
        });
      }
    };
    initializeAmplitude()
  }, []);

  // Set Amplitude user when available
  useEffect(() => {
    if (userId && userData?.account?.email) {
      amplitude.setUserId(userId);
      const identifyObj = new amplitude.Identify().set("email", userData.account.email);
      amplitude.identify(identifyObj);
    }
  }, [userId, userData])

  useEffect(() => {
    dispatch(fetchFlags())
    // eslint-disable-next-line
  }, [])
  useEffect(() => {
    // Fetch IP once when app loads
    dispatch(fetchPublicIP())
  }, [dispatch])
  if (!isOnline) {
    return <NoInternetPage />;
  }

  if (loading || flags === null) {
    return <HomePageSkeleton />;
  }

  if (!loading && flags?.maintenance) {
    return <MaintenancePage />;
  }
  return (
    <>
      <BrowserRouter>
        <AuthProvider>
          <HelmetProvider>
            <ErrorModal />
            <ScrollToTop />
            <Router/>
          </HelmetProvider>
        </AuthProvider>
      </BrowserRouter>
    </>
  );
}
export default App;
